import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Mail } from 'lucide-react';
import { useCriarUsuario } from '@/services/Configuracoes';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PERFIS = ['Admin', 'Contador', 'Auxiliar', 'Visualizador'];

export function NovoUsuarioDialog({ open, onOpenChange }: Props) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [perfil, setPerfil] = useState('Visualizador');
  const { mutate, isPending, isError, data, reset } = useCriarUsuario();

  const failed = isError || data?.status.resultado === false;

  function resetState() {
    setNome('');
    setEmail('');
    setPerfil('Visualizador');
    reset();
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  }

  function handleSubmit() {
    if (!nome || !email) return;
    // Sem senha aqui: o usuário nasce pendente e define a própria senha pelo
    // link de convite (mesmo mecanismo do "esqueci minha senha").
    mutate(
      { EmailLogin: email, Nome: nome, Perfil: perfil, Status: 'A' },
      { onSuccess: response => response.status.resultado && handleOpenChange(false) }
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Novo Usuário</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start gap-2 rounded-md border border-muted bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              O usuário recebe um e-mail de convite e define a própria senha no primeiro acesso.
            </span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="novo-usuario-nome">Nome</Label>
            <Input
              id="novo-usuario-nome"
              value={nome}
              onChange={e => setNome(e.target.value)}
              disabled={isPending}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-usuario-email">E-mail</Label>
            <Input
              id="novo-usuario-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isPending}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-usuario-perfil">Perfil</Label>
            <Select value={perfil} onValueChange={setPerfil} disabled={isPending}>
              <SelectTrigger id="novo-usuario-perfil" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERFIS.map(p => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {failed && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{data?.status.mensagem ?? 'Não foi possível enviar o convite.'}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!nome || !email || isPending}>
            {isPending ? 'Enviando...' : 'Enviar convite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
